import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, ChevronLeft, ChevronRight, Loader2, Plus, Ban, Check, X, RefreshCw,
  Tag,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";
import {
  USAGE_PURPOSES, UsagePurposeBadge, noteRequiredFor, purposeMeta,
} from "./UsagePurpose";

const money = (v) => (v == null ? "—"
  : `€${Number(v).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const uid = () => `op_${Math.random().toString(36).slice(2, 12)}`;

const Figure = ({ label, value, pct, tone }) => (
  <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-sm font-mono ${tone || "text-white"}`}>
      {money(value)}{pct != null && <span className="text-slate-500 ml-1">{pct.toFixed(1)}%</span>}
    </p>
  </div>
);

export const JobCostDrawer = ({ logId, onClose, onSaved, onStep, stepInfo }) => {
  const [detail, setDetail] = useState(null);
  const [pending, setPending] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [units, setUnits] = useState([]);
  const [matForm, setMatForm] = useState({
    unit_id: "", amount_used: "", deduct_stock: true,
    usage_purpose: "customer_job", usage_note: "" });
  const [manual, setManual] = useState({ description: "", amount: "" });
  // Blank = keep whatever the line already has (or price it from the rule).
  const [washCost, setWashCost] = useState("");
  const [override, setOverride] = useState({ amount: "", reason: "" });
  const [techId, setTechId] = useState("");
  // Reclassify a SAVED line. Metadata only — the backend never moves
  // stock for this op, which is the whole point.
  const [purposeEdit, setPurposeEdit] = useState(null);
  const [impact, setImpact] = useState(null);

  const load = useCallback(async () => {
    setPending([]);
    setPreview(null);
    try {
      const r = await axios.get(`${API}/job-logs/${logId}/cost-detail`);
      setDetail(r.data);
      setOverride({ amount: r.data.labour?.override?.amount ?? "",
                    reason: r.data.labour?.override?.reason ?? "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the job costs");
    }
  }, [logId]);

  useEffect(() => { setImpact(null); setTechId(""); setPurposeEdit(null); }, [logId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/stock/units`)
      .then((r) => setUnits(Array.isArray(r.data) ? r.data : (r.data.units || [])))
      .catch(() => setUnits([]));
  }, []);

  const queue = async (op) => {
    const next = [...pending, op];
    setPending(next);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/job-logs/${logId}/costs/preview`,
                                 { operations: next });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "That change is not possible");
      setPending(pending);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!pending.length) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/job-logs/${logId}/costs`,
                                  { operations: pending });
      setImpact(r.data.impact || []);
      toast.success((r.data.impact || [])[0]?.message
        || `Costs updated — the P&L for ${detail.header.invoice_month} has moved`);
      onSaved?.(r.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const shown = preview?.after || detail?.computed;
  const h = detail?.header;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right"
                    className="bg-slate-900 border-slate-700 w-full sm:max-w-xl overflow-y-auto"
                    data-testid="job-cost-drawer">
        <SheetHeader>
          <SheetTitle className="text-white text-base">
            {h ? `${h.customer_name || "Job"} · ${(h.service_names || []).join(", ")}` : "Loading…"}
          </SheetTitle>
        </SheetHeader>
        {!detail && <Loader2 className="w-5 h-5 animate-spin text-saturn-orange mt-6" />}
        {detail && (
          <div className="space-y-4 mt-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <p className="text-slate-400">
                {[h.vehicle?.make, h.vehicle?.model || h.vehicle?.model_other,
                  h.vehicle?.reg].filter(Boolean).join(" · ")}
                {" · "}{h.technician_name || "no technician"}
                {h.duration_minutes ? ` · ${h.duration_minutes} min` : ""}
                {" · "}{money(h.net_revenue)} ex-VAT
              </p>
              {stepInfo && (
                <span className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                          disabled={!stepInfo.hasPrev} onClick={() => onStep(-1)}
                          data-testid="job-cost-prev">
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <span className="text-slate-500" data-testid="job-cost-step">
                    {stepInfo.index + 1}/{stepInfo.total}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                          disabled={!stepInfo.hasNext} onClick={() => onStep(1)}
                          data-testid="job-cost-next">
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="job-cost-figures">
              <Figure label="Materials" value={shown?.material_cost_sum} />
              <Figure label="Labour" value={shown?.labour_cost} />
              <Figure label="Total cost" value={shown?.total_job_cost} tone="text-orange-400" />
              <Figure label="Margin" value={shown?.margin} pct={shown?.margin_pct}
                      tone={(shown?.margin ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"} />
            </div>
            {preview && (
              <p className="text-[11px] text-saturn-orange" data-testid="job-cost-preview-flag">
                Preview of {pending.length} unsaved change(s) — press Save to apply.
              </p>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Materials</p>
              {detail.materials.filter((m) => !m.removed).map((m, i) => (
                <div key={`${m.unit_id}-${i}`}
                     className="flex justify-between gap-2 border-b border-slate-800 py-1.5"
                     data-testid={`job-cost-material-${i}`}>
                  <span className="text-slate-300">
                    {m.needs_cost && <span className="text-red-400 mr-1" title="No cost recorded"
                                           data-testid={`job-cost-material-flag-${i}`}>●</span>}
                    {m.product_name} · {m.amount_used}{m.base_unit}
                    {m.cost_source && <span className="text-slate-500"> · {m.cost_source}</span>}
                    <UsagePurposeBadge purpose={m.usage_purpose} note={m.usage_note}
                                       testId={`job-cost-material-purpose-${i}`} />
                    {m.usage_note && (
                      <span className="text-slate-500"> · {m.usage_note}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`font-mono ${m.counts_towards_job_cost === false
                      ? "text-slate-500 line-through" : "text-white"}`}>
                      {money(m.material_cost)}
                    </span>
                    <button className="text-slate-400 hover:text-white"
                            title="Change what this was used for (does not move stock)"
                            onClick={() => setPurposeEdit({
                              index: i, unit_id: m.unit_id,
                              product_name: m.product_name,
                              usage_purpose: m.usage_purpose || "customer_job",
                              usage_note: m.usage_note || "" })}
                            data-testid={`job-cost-material-purpose-edit-${i}`}>
                      <Tag className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              ))}
              {purposeEdit && (
                <div className="mt-2 rounded border border-sky-500/40 bg-sky-500/5 p-2 space-y-2"
                     data-testid="job-cost-purpose-editor">
                  <p className="text-[11px] text-slate-300">
                    What was <strong>{purposeEdit.product_name}</strong> used for?
                  </p>
                  <select value={purposeEdit.usage_purpose}
                          onChange={(e) => setPurposeEdit({
                            ...purposeEdit, usage_purpose: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1.5"
                          data-testid="job-cost-purpose-select">
                    {USAGE_PURPOSES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  {noteRequiredFor(purposeEdit.usage_purpose) && (
                    <Input value={purposeEdit.usage_note}
                           onChange={(e) => setPurposeEdit({
                             ...purposeEdit, usage_note: e.target.value })}
                           placeholder="Required — what was it for?"
                           className="bg-slate-900 border-slate-700 text-white h-8"
                           data-testid="job-cost-purpose-note" />
                  )}
                  <p className="text-[10px] text-slate-500">
                    {purposeMeta(purposeEdit.usage_purpose).help} Stock is not
                    moved, restored or re-deducted — only who it is charged to changes.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 bg-sky-600 hover:bg-sky-500 text-white"
                            disabled={busy || (noteRequiredFor(purposeEdit.usage_purpose)
                                               && !purposeEdit.usage_note.trim())}
                            onClick={() => {
                              queue({ op: "set_usage_purpose",
                                      index: purposeEdit.index,
                                      unit_id: purposeEdit.unit_id,
                                      usage_purpose: purposeEdit.usage_purpose,
                                      usage_note: purposeEdit.usage_note.trim() || null });
                              setPurposeEdit(null);
                            }}
                            data-testid="job-cost-purpose-apply-btn">
                      Apply
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 border-slate-600 text-slate-300"
                            onClick={() => setPurposeEdit(null)}
                            data-testid="job-cost-purpose-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {detail.internal_use_cost_excluded && (
                <p className="text-[11px] text-sky-300 mt-1"
                   data-testid="job-cost-internal-excluded">
                  {money(detail.internal_use_cost_excluded)} of internal use is
                  excluded from this job&apos;s cost and margin.
                </p>
              )}
              {detail.manual_costs.filter((c) => !c.removed && c.source !== "wash_perk").map((c) => (
                <div key={c.entry_id}
                     className="flex justify-between gap-2 border-b border-slate-800 py-1.5"
                     data-testid={`job-cost-manual-${c.entry_id}`}>
                  <span className="text-slate-300">
                    {c.description} <span className="text-slate-500">· other cost</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-white">{money(c.amount)}</span>
                    <button className="text-red-400" title="Remove"
                            onClick={() => queue({ op: "remove_manual_cost",
                                                   entry_id: c.entry_id })}
                            data-testid={`job-cost-manual-remove-${c.entry_id}`}>
                      <Ban className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              ))}
              {detail.materials_confirmed_none && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid="job-cost-confirmed-none">
                  Confirmed: no stock materials used ({detail.materials_confirmed_none_by})
                </p>
              )}

              {/* Complimentary car wash — the external company's charge.
                  One line per job: add it, reprice it, or "wash not
                  used" to take it off. */}
              {(detail.wash_perk?.entitled || detail.wash_perk?.line) && (
                <div className="mt-2 rounded border border-cyan-800/60 bg-cyan-500/5 p-2 space-y-1.5"
                     data-testid="job-cost-wash-block">
                  <div className="flex justify-between gap-2 items-center">
                    <span className="text-cyan-200">
                      External car wash
                      {detail.wash_perk?.line?.wash_rule_name && (
                        <span className="text-slate-500"> · {detail.wash_perk.line.wash_rule_name}</span>
                      )}
                    </span>
                    <span className="font-mono text-white"
                          data-testid="job-cost-wash-amount">
                      {detail.wash_perk?.line ? money(detail.wash_perk.line.amount) : "—"}
                    </span>
                  </div>
                  {detail.wash_perk?.line && (
                    <p className="text-[11px] text-slate-400">
                      {money(detail.wash_perk.line.wash_cost_net)} net
                      {" @ "}{Number(detail.wash_perk.line.wash_vat_rate_percent ?? 0)}% VAT
                      {" = "}{money(detail.wash_perk.line.wash_vat_amount)}
                      {detail.wash_perk.line.wash_vat_reclaimable
                        ? " (reclaimable)" : " (not reclaimable)"}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input type="number" min="0" step="0.01" value={washCost}
                           onChange={(e) => setWashCost(e.target.value)}
                           placeholder={detail.wash_perk?.line
                             ? String(detail.wash_perk.line.wash_cost_net)
                             : String(detail.wash_perk?.wash_cost_net ?? "")}
                           className="bg-slate-900 border-slate-700 text-white h-8 w-28"
                           data-testid="job-cost-wash-input" />
                    <Button size="sm" variant="outline"
                            className="h-8 border-cyan-700 text-cyan-300"
                            onClick={() => queue({
                              op: "set_wash_cost", present: true,
                              cost_net: washCost === "" ? undefined : Number(washCost),
                            })}
                            data-testid="job-cost-wash-set-btn">
                      {detail.wash_perk?.line ? "Update wash cost" : "Add wash cost"}
                    </Button>
                    {detail.wash_perk?.line && (
                      <Button size="sm" variant="outline"
                              className="h-8 border-red-700 text-red-300"
                              onClick={() => queue({ op: "set_wash_cost",
                                                     present: false,
                                                     reason: "wash not used" })}
                              data-testid="job-cost-wash-not-used-btn">
                        Wash not used
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded border border-slate-800 bg-slate-800/30 p-2 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Select value={matForm.unit_id}
                        onValueChange={(v) => setMatForm({ ...matForm, unit_id: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-8"
                                 data-testid="job-cost-unit-select">
                    <SelectValue placeholder="Stock unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.filter((u) => u.status !== "written_off").map((u) => (
                      <SelectItem key={u.unit_id} value={u.unit_id}>
                        {u.product_name} · {u.remaining_quantity}{u.base_unit} left
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" step="0.01" placeholder="Amount used"
                       value={matForm.amount_used}
                       onChange={(e) => setMatForm({ ...matForm, amount_used: e.target.value })}
                       className="bg-slate-900 border-slate-700 text-white h-8"
                       data-testid="job-cost-amount-input" />
                <label className="flex items-center gap-2 text-slate-400">
                  <input type="checkbox" checked={matForm.deduct_stock}
                         onChange={(e) => setMatForm({ ...matForm, deduct_stock: e.target.checked })}
                         data-testid="job-cost-deduct-toggle" />
                  Also deduct from stock
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select value={matForm.usage_purpose}
                        onChange={(e) => setMatForm({ ...matForm, usage_purpose: e.target.value })}
                        className="bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 h-8"
                        data-testid="job-cost-new-purpose-select">
                  {USAGE_PURPOSES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {noteRequiredFor(matForm.usage_purpose) && (
                  <Input value={matForm.usage_note}
                         onChange={(e) => setMatForm({ ...matForm, usage_note: e.target.value })}
                         placeholder="Required — what was it for?"
                         className="bg-slate-900 border-slate-700 text-white h-8"
                         data-testid="job-cost-new-purpose-note" />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="h-7 bg-saturn-orange text-black hover:bg-saturn-orange/80"
                        disabled={busy || !matForm.unit_id || !matForm.amount_used
                                  || (noteRequiredFor(matForm.usage_purpose)
                                      && !matForm.usage_note.trim())}
                        onClick={() => queue({ op: "add_stock_material", op_id: uid(),
                                               unit_id: matForm.unit_id,
                                               amount_used: Number(matForm.amount_used),
                                               deduct_stock: matForm.deduct_stock,
                                               usage_purpose: matForm.usage_purpose,
                                               usage_note: matForm.usage_note || null })}
                        data-testid="job-cost-add-material-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add stock material
                </Button>
                <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                        disabled={busy} onClick={() => queue({ op: "recost_lines" })}
                        data-testid="job-cost-recost-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Re-cost from stock
                </Button>
                <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                        disabled={busy} onClick={() => queue({ op: "confirm_no_materials" })}
                        data-testid="job-cost-no-materials-btn">
                  No stock materials used
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input placeholder="Other cost description" value={manual.description}
                       onChange={(e) => setManual({ ...manual, description: e.target.value })}
                       className="bg-slate-900 border-slate-700 text-white h-8"
                       data-testid="job-cost-manual-desc" />
                <Input type="number" step="0.01" placeholder="Amount" value={manual.amount}
                       onChange={(e) => setManual({ ...manual, amount: e.target.value })}
                       className="bg-slate-900 border-slate-700 text-white h-8"
                       data-testid="job-cost-manual-amount" />
                <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                        disabled={busy || !manual.description || !manual.amount}
                        onClick={() => queue({ op: "add_manual_cost",
                                               description: manual.description,
                                               amount: Number(manual.amount) })}
                        data-testid="job-cost-add-manual-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add other cost
                </Button>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Labour</p>
              {!detail.header.technician_id && (
                <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2 mb-2 space-y-2"
                     data-testid="job-cost-no-technician">
                  <p className="flex gap-2 items-start text-amber-200">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    No technician recorded on this job. Pick who did it and labour is
                    computed from their pay config.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Select value={techId} onValueChange={setTechId}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-8"
                                     data-testid="job-cost-technician-select">
                        <SelectValue placeholder="Who did this job?" />
                      </SelectTrigger>
                      <SelectContent>
                        {(detail.technician_options || []).map((t) => (
                          <SelectItem key={t.user_id} value={t.user_id}
                                      disabled={!t.pay_configured}>
                            {t.name}
                            {t.pay_configured
                              ? ` · ${t.pay_model} @ ${t.pay_rate}`
                              : " · no pay rate set"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline"
                            className="h-8 border-slate-600 text-slate-200"
                            disabled={busy || !techId}
                            onClick={() => queue({ op: "set_technician",
                                                   technician_id: techId })}
                            data-testid="job-cost-set-technician-btn">
                      Assign technician
                    </Button>
                  </div>
                </div>
              )}
              {detail.labour.pay_configured ? (
                <p className="text-slate-300" data-testid="job-cost-labour-model">
                  {detail.labour.pay_model} @ {detail.labour.pay_rate} →
                  {" "}{money(detail.labour.computed)}
                  {detail.labour.pay_model_used === "override" && (
                    <span className="text-amber-300"> (override in use)</span>
                  )}
                </p>
              ) : (
                <p className="flex gap-2 items-start rounded border border-amber-500/40 bg-amber-500/5 text-amber-200 px-2 py-1.5"
                   data-testid="job-cost-labour-missing">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  {detail.header.technician_name || "This technician"} has no pay model or
                  rate set. Fix it in Staff Management for future jobs, or set a one-off
                  override below.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <Input type="number" step="0.01" placeholder="Labour override €"
                       value={override.amount}
                       onChange={(e) => setOverride({ ...override, amount: e.target.value })}
                       className="bg-slate-900 border-slate-700 text-white h-8"
                       data-testid="job-cost-override-amount" />
                <Input placeholder="Reason (required)" value={override.reason}
                       onChange={(e) => setOverride({ ...override, reason: e.target.value })}
                       className="bg-slate-900 border-slate-700 text-white h-8"
                       data-testid="job-cost-override-reason" />
                <Button size="sm" variant="outline" className="h-7 border-slate-600 text-slate-200"
                        disabled={busy || !override.amount || !override.reason}
                        onClick={() => queue({ op: "set_labour_override",
                                               amount: Number(override.amount),
                                               reason: override.reason })}
                        data-testid="job-cost-override-btn">
                  Apply override
                </Button>
              </div>
            </div>

            {impact?.length > 0 && (
              <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 space-y-1"
                   data-testid="job-cost-impact">
                {impact.map((i) => (
                  <p key={i.month} className="text-emerald-200">{i.message}</p>
                ))}
              </div>
            )}
            <p className="text-[11px] text-amber-200/80" data-testid="job-cost-note">
              {detail.note}
            </p>
            <div className="flex gap-2 pb-6">
              <Button size="sm" disabled={busy || !pending.length} onClick={save}
                      className="bg-saturn-orange text-black hover:bg-saturn-orange/80"
                      data-testid="job-cost-save-btn">
                <Check className="w-3 h-3 mr-1" /> Save changes
              </Button>
              <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                      onClick={() => load()} disabled={busy || !pending.length}
                      data-testid="job-cost-discard-btn">
                <X className="w-3 h-3 mr-1" /> Discard
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default JobCostDrawer;
